import { Button, Select, type Column, type SelectOption } from "@legal-500/legal500-design-system";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { PageHeader } from "@/components/PageHeader";
import { DataTable } from "@/components/DataTable/DataTable";
import { ConfirmationModal } from "@/components/Modal/ConfirmationModal";
import { PaginationControls } from "@/components/PaginationControls";
import {
  FirmChip,
  ProfilesFilterCell,
  ProfilesListFilters,
  RegionNodeChip,
  useCompanyNames,
} from "@/components/Profiles/ListFilters";
import { teamPageKeys, teamPagesService, type TeamPage } from "@/api/profiles";
import { parseRegionKind } from "@/hooks/useRegions";
import { ROUTES } from "@/routes";
import { removeTeamPage } from "./teamPagesMutations";
import styles from "../ProfilesList.module.css";

const PAGE_SIZE = 20;

/**
 * The profiles-api accepts `"team" | "clerks"` as pageType values. The shared
 * staff-ui `PageType` alias still reads `"meet_the_team" | "meet_the_clerks"`
 * (legacy) which is unused by the API; we keep the actual API enum here as a
 * narrow string and cast when handing to the service.
 */
type PageTypeEnum = "team" | "clerks";

const PAGE_TYPE_OPTIONS: SelectOption[] = [
  { value: "", label: "All page types" },
  { value: "team", label: "Teams" },
  { value: "clerks", label: "Clerks" },
];

const PAGE_TYPE_LABEL: Record<string, string> = {
  team: "Teams",
  teams: "Teams",
  clerks: "Clerks",
};

function formatDateShort(iso: string | undefined | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleDateString();
}

export function TeamPagesList() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  const companyId = searchParams.get("companyId") ?? undefined;
  const status = searchParams.get("status") ?? undefined;
  const q = searchParams.get("q") ?? undefined;
  const regionKind = parseRegionKind(searchParams.get("regionKind"));
  const regionId = searchParams.get("regionId") ?? undefined;
  const pageType = searchParams.get("pageType") ?? "";
  const page = Math.max(1, Number.parseInt(searchParams.get("page") ?? "1", 10) || 1);
  const offset = (page - 1) * PAGE_SIZE;

  const [deleteTarget, setDeleteTarget] = useState<TeamPage | null>(null);

  const filters = useMemo(
    () => ({
      companyId,
      status,
      q,
      regionKind,
      regionId,
      // pageType is cast for the service signature mismatch (see comment above).
      pageType: (pageType || undefined) as PageTypeEnum | undefined,
      limit: PAGE_SIZE,
      offset,
    }),
    [companyId, status, q, regionKind, regionId, pageType, offset],
  );

  // `q` is persisted in the URL so deep links round-trip, forwarded to the API
  // as `search`, and kept in the query key for cache distinction.
  const queryKey = teamPageKeys.list({
    companyId,
    status,
    q,
    regionKind,
    regionId,
    pageType,
    offset,
  });

  const listQuery = useQuery({
    queryKey,
    queryFn: () =>
      teamPagesService.list(
        // The staff-ui TeamPageFilters.pageType is typed against the stale
        // PageType alias. The service stringifies the value into the URL, so a
        // localised cast is safe and avoids a wider refactor.
        filters as unknown as Parameters<typeof teamPagesService.list>[0],
      ),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => removeTeamPage(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: teamPageKeys.all });
      setDeleteTarget(null);
    },
  });

  const rows = listQuery.data?.data ?? [];
  const total = listQuery.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // Batch firm-name resolution so each row's chip doesn't N+1.
  const companyIds = useMemo(() => rows.map(r => r.companyId), [rows]);
  const names = useCompanyNames(companyIds);

  function setParam(key: string, value: string | null) {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (value) next.set(key, value);
      else next.delete(key);
      // Reset page when filters change.
      if (key !== "page") next.delete("page");
      return next;
    });
  }

  const displayNameOf = (row: TeamPage) => row.teamName || `Team page ${row.id.slice(0, 8)}`;

  // Columns for the shared <DataTable>. Columns that don't map to a single
  // field (Regions, Actions) use a valid `keyof TeamPage` accessor purely to
  // satisfy the type and supply a `render`. The Regions column opts into `wrap`
  // so the region chips can flow onto multiple lines.
  const columns: Column<TeamPage>[] = [
    {
      header: "Firm",
      accessor: "companyId",
      render: (_value, row) => <FirmChip companyId={row.companyId} name={names[row.companyId]} />,
    },
    {
      header: "Title",
      accessor: "teamName",
      render: (_value, row) => (
        <span data-testid={`team-page-row-${row.id}`}>
          <Link to={ROUTES.profiles.teamPageDetail(row.id)} className={styles.titleLink}>
            {displayNameOf(row)}
          </Link>
        </span>
      ),
    },
    {
      header: "Type",
      accessor: "pageType",
      render: (_value, row) => PAGE_TYPE_LABEL[String(row.pageType)] ?? String(row.pageType),
    },
    {
      header: "Regions",
      accessor: "regions",
      wrap: true,
      render: (_value, row) => (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
          {(row.regions ?? []).map(r => (
            <RegionNodeChip key={`${r.kind}:${r.id}`} region={r} />
          ))}
        </div>
      ),
    },
    {
      header: "Status",
      accessor: "status",
      render: (_value, row) => (
        <span className={styles.statusPill} data-status={row.status}>
          {row.status}
        </span>
      ),
    },
    {
      header: "Updated",
      accessor: "updatedAt",
      render: (_value, row) => (
        <span className={styles.muted}>{formatDateShort(row.updatedAt)}</span>
      ),
    },
    {
      header: "",
      accessor: "id",
      render: (_value, row) => {
        const display = displayNameOf(row);
        return (
          <div className={styles.actions}>
            <Button
              variant="outlined"
              size="small"
              aria-label={`View ${display}`}
              onClick={() => navigate(ROUTES.profiles.teamPageDetail(row.id))}
            >
              View
            </Button>
            <Button
              variant="outlined"
              size="small"
              aria-label={`Edit ${display}`}
              onClick={() => navigate(ROUTES.profiles.teamPageDetail(row.id))}
            >
              Edit
            </Button>
            <Button
              variant="text"
              color="error"
              size="small"
              aria-label={`Delete ${display}`}
              onClick={() => setDeleteTarget(row)}
            >
              Delete
            </Button>
          </div>
        );
      },
    },
  ];

  return (
    <div className={styles.wrapper}>
      <PageHeader title="Team pages">
        <Button
          variant="contained"
          color="primary"
          onClick={() => navigate(ROUTES.profiles.teamPageNew)}
        >
          New team page
        </Button>
      </PageHeader>

      <ProfilesListFilters enabledFilters={{ company: true, status: true, q: true, region: true }}>
        <ProfilesFilterCell>
          <Select
            label="Page type"
            value={pageType}
            options={PAGE_TYPE_OPTIONS}
            onChange={e => setParam("pageType", e.target.value || null)}
            size="small"
          />
        </ProfilesFilterCell>
      </ProfilesListFilters>

      <div className={styles.contentSection}>
        {listQuery.isPending ? (
          <p role="status" className={styles.muted}>
            Loading team pages…
          </p>
        ) : listQuery.isError ? (
          <p role="alert" className={styles.errorBanner}>
            {listQuery.error instanceof Error
              ? listQuery.error.message
              : "Failed to load team pages"}
          </p>
        ) : rows.length === 0 ? (
          <p className={styles.emptyState}>No team pages match your filters.</p>
        ) : (
          <>
            <DataTable data={rows} columns={columns} rowKey="id" />

            <PaginationControls
              currentPage={page}
              totalPages={totalPages}
              startIndex={offset}
              endIndex={offset + rows.length}
              totalCount={total}
              itemName="team pages"
              onPageChange={p => setParam("page", String(p))}
            />
          </>
        )}
      </div>

      {deleteTarget && (
        <ConfirmationModal
          title="Delete team page"
          message={`Are you sure you want to delete '${deleteTarget.teamName || deleteTarget.id}'?`}
          warningMessage="This action cannot be undone."
          confirmButtonText="Delete"
          confirmButtonColor="error"
          cancelButtonText="Cancel"
          onClose={() => setDeleteTarget(null)}
          onConfirm={async () => {
            await deleteMutation.mutateAsync(deleteTarget.id);
          }}
        />
      )}
    </div>
  );
}

export default TeamPagesList;
